import { useEffect } from 'react';
import { MotionConfig } from 'framer-motion';
import { BrowserRouter } from 'react-router-dom';
import { AppRouter } from './app/router/AppRouter';
import { useUIStore } from './stores/uiStore';
import { useAuthStore } from './stores/authStore';
import { useDepartmentsStore } from './stores/departmentsStore';
import { useDocumentsStore } from './stores/documentsStore';
import { useUsersStore } from './stores/usersStore';
import { RUNTIME } from './config/runtime';
import { fetchCloudSnapshot } from './services/cloud';
import { ScannerOverlay } from './features/scanner/ScannerOverlay';
import { PageCustomizer } from './features/customization/PageCustomizer';
import { PwaUpdatePrompt } from './components/feedback/PwaUpdatePrompt';
import './config/i18n';

export default function App() {
  const { lang, theme } = useUIStore();
  const hydrateAuth = useAuthStore(state => state.hydrate);
  const authInitialized = useAuthStore(state => state.initialized);
  const user = useAuthStore(state => state.user);
  const replaceDepartments = useDepartmentsStore(state => state.replaceAll);
  const replaceDocuments = useDocumentsStore(state => state.replaceAll);
  const replaceUsers = useUsersStore(state => state.replaceAll);
  const showToast = useUIStore(state => state.showToast);
  const hydrateNotifications = useUIStore(state => state.hydrateNotifications);
  const clearNotifications = useUIStore(state => state.clearNotifications);

  useEffect(() => { void hydrateAuth(); }, [hydrateAuth]);

  useEffect(() => {
    if (RUNTIME.mode !== 'cloud' || !authInitialized || !user) return;
    let active = true;
    fetchCloudSnapshot(user.role === 'system_admin')
      .then(snapshot => {
        if (!active) return;
        replaceDepartments(snapshot.departments);
        replaceDocuments(snapshot.documents, snapshot.qrRecords);
        if (snapshot.users.length) replaceUsers(snapshot.users);
      })
      .catch(() => {
        if (active) showToast(lang === 'ar' ? 'تعذر مزامنة بيانات Cloud؛ أُبقيت البيانات المحلية الحالية.' : 'Cloud synchronization failed; current local data was preserved.', 'warning');
      });
    return () => { active = false; };
  }, [authInitialized, user, replaceDepartments, replaceDocuments, replaceUsers, showToast, lang]);

  useEffect(() => {
    if (!authInitialized) return;
    if (user) void hydrateNotifications(user.id);
    else clearNotifications();
  }, [authInitialized, user, hydrateNotifications, clearNotifications]);

  useEffect(() => {
    document.documentElement.dir  = lang === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;

    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) {
      meta.content = theme === 'light' ? '#F7F2E8' : '#020C1B';
    }
  }, [theme]);

  return (
    // reducedMotion="user" makes framer-motion honour the OS/browser
    // prefers-reduced-motion setting (the CSS layer already does; the JS
    // animation layer did not until now) — an accessibility improvement that
    // also makes animation-heavy flows deterministic under automation.
    <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <AppRouter />
        <ScannerOverlay />
        <PageCustomizer />
        {/* The update / offline-ready prompt belongs to the authenticated shell;
            keeping it out of the login surface prevents notification overlap. */}
        {user && <PwaUpdatePrompt />}
      </BrowserRouter>
    </MotionConfig>
  );
}
